import React, { FC, MouseEvent } from "react";

// I18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

// services
import { useServiceFeesFeature } from "../../hooks/useServiceFeesFeature";

// UI
import { Card, CardProps } from "@UI/Components/Card/Card";
import { Icon } from "@UI/Components/Icon/Icon";
import { Text } from "@UI/Components/Text/Text";
import { Flex } from "@UI/Components/Flex/Flex";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";

import { ArrowRight } from "@UI/Components/Icons/ArrowRight";

// messages
import messages from "./messages";

// Core
import { Rate } from "@Shared/Components/Rate/Rate";
import useThemeToken from "@UI/hooks/useThemeToken";
import { Bold } from "@UI/Components/Bold/Bold";
import { ID } from "@types";
import { useServiceOffer } from "@modules/ServiceAssignment/hooks/useServiceOffer";

interface Props extends CardProps {
  serviceOfferId: ID;
  onClick: (event: MouseEvent<HTMLDivElement>) => void;
}

export const ServiceFeeSubscriptionCTA: FC<Props> = ({
  serviceOfferId,
  ...props
}) => {
  const { serviceOffer } = useServiceOffer(serviceOfferId);

  const { serviceFeesFeature, getClientServiceFee } = useServiceFeesFeature();
  const pink500 = useThemeToken("colors", "pink.500");
  const orange500 = useThemeToken("colors", "orange.500");
  const pink600 = useThemeToken("colors", "pink.600");
  const orange600 = useThemeToken("colors", "orange.600");

  if (!serviceFeesFeature) {
    return <LoadingScreen />;
  }

  const serviceFeeCents = getClientServiceFee(serviceOffer.rateCents);

  const formattedServiceFee = (
    <Rate
      unit={serviceOffer.billingUnit}
      cents={serviceFeeCents}
      currency={serviceOffer.rateCurrency}
    />
  );

  return (
    <Card
      shadow={null}
      paddingX={["3", "3", "4"]}
      paddingY={["3", "3", "3"]}
      background={`linear-gradient(96.51deg, ${pink500} 0%, ${orange500} 99.87%);`}
      _hover={{
        background: `linear-gradient(96.51deg, ${pink600} 0%, ${orange600} 99.87%);`,
      }}
      cursor="pointer"
      {...props}
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Text fontSize="md" textAlign="left" color="white" mb="0">
          <FormattedMessage
            {...messages.getASubscriptionAndSaveXInServiceFees}
            values={{
              serviceFee: formattedServiceFee,
              bold: (...chunks) => <Bold color="white">{chunks}</Bold>,
            }}
          />
        </Text>
        <Icon
          as={ArrowRight}
          display="block"
          marginLeft="2"
          width="4"
          height="4"
          color="white"
        />
      </Flex>
    </Card>
  );
};
